import { useMemo } from 'react';
import { useApp } from '@context/AppContext';
import { useRelay } from './useRelay';
import GameHUD from '../shared/GameHUD';
import RelayReactions from './RelayReactions';

const REACTION_EMOJIS = ['😂', '😍', '🔥', '👀', '🤯', '✏️'];

/**
 * Relay active phase — drawer sees full canvas + HUD, spectators watch + react.
 */
export default function RelayActive() {
  const { state } = useApp();
  const { game, user, room } = state;
  const { advanceTurn, sendReaction } = useRelay();

  const isDrawer = game.currentDrawerId === user.id;
  const drawerName = room.members?.[game.currentDrawerId]?.nickname || 'Unknown';

  // Find who's next
  const nextDrawer = useMemo(() => {
    if (!game.turnOrder) return null;
    const currentIdx = game.turnOrder.indexOf(game.currentDrawerId);
    const nextIdx = (currentIdx + 1) % game.turnOrder.length;
    const nextId = game.turnOrder[nextIdx];
    return room.members?.[nextId]?.nickname || 'Unknown';
  }, [game.turnOrder, game.currentDrawerId, room.members]);

  // Calculate progress
  const currentTurn = game.turnOrder
    ? game.turnOrder.indexOf(game.currentDrawerId) + 1 + ((game.round - 1) * game.turnOrder.length)
    : 0;
  const totalTurns = (game.turnOrder?.length || 0) * (game.totalRounds || 1);

  // Check if user is next (within 10 seconds of their turn)
  const isNextUp = useMemo(() => {
    if (!game.turnOrder || isDrawer) return false;
    const currentIdx = game.turnOrder.indexOf(game.currentDrawerId);
    const nextIdx = (currentIdx + 1) % game.turnOrder.length;
    return game.turnOrder[nextIdx] === user.id;
  }, [game.turnOrder, game.currentDrawerId, user.id, isDrawer]);

  // Only host handles timer expiry
  const handleTimerExpire = user.isHost ? advanceTurn : undefined;

  return (
    <>
      <GameHUD
        timerEndsAt={game.timerEndsAt}
        timerDuration={game.timerDuration}
        onTimerExpire={handleTimerExpire}
        emoji="🔄"
        label={isDrawer ? "You're drawing!" : `${drawerName} is drawing`}
        sublabel={`Turn ${currentTurn} of ${totalTurns} — Next: ${nextDrawer}`}
      >
        {/* Bottom bar content */}
        {isDrawer ? (
          /* Drawer: show theme if set */
          game.theme ? (
            <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg border border-slate-100">
              <p className="text-xs text-slate-400 font-display">Theme</p>
              <p className="text-sm font-semibold text-charcoal font-display">{game.theme}</p>
            </div>
          ) : null
        ) : (
          /* Spectator: reaction bar */
          <div className="bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2 shadow-lg border border-slate-100 flex items-center gap-1.5">
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => sendReaction(emoji)}
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors text-xl active:scale-90"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </GameHUD>

      {/* "You're next!" warning for spectators */}
      {isNextUp && (
        <div className="fixed top-32 right-4 z-50 animate-bounce">
          <div className="bg-amber text-white px-4 py-2 rounded-xl shadow-lg text-sm font-semibold font-display">
            You&apos;re next!
          </div>
        </div>
      )}

      {/* Floating reactions overlay */}
      <RelayReactions />
    </>
  );
}
